import React, { useRef, useState, useEffect } from "react";
import { Circle, Group, Line, Rect, Text } from "react-konva";

const NormalColumn = (props) => {
    const [hoverColumn, setHoverColumn] = useState(false);
    const [textWidth, setTextWidth] = useState(0);
    const textRef = useRef();

    const {
        position,
        handleDragMove,
        getDynamicFontSize,
        groupRef,
        positionAngle,
        dxfScale,
        stageYUp
    } = props;
    const isLocked = Boolean(props.locked);
    const handleSelect = () => {
        if (!isLocked) {
            props.selectColumn(props.id);
        }
    };

    const scaledX = position.x * dxfScale;
    const scaledY = position.y * dxfScale;


    const outreach = Number(props.values?.outreach) || 0;
    // 0.5m diameter visual minimum (0.25m radius), aligned with mast center.
    const columnRadius = Math.max(0.25, Number(props.circleRadius || 1) / 2);
    const luminaireOffset = Math.max(0, outreach) * dxfScale;
    const effectiveArmLength = Math.max(columnRadius, luminaireOffset);
    const rectArmLength = luminaireOffset;
    const luminaireMarkerSize = Math.max(0.35 * dxfScale, columnRadius * 0.35);
    const armStrokeWidth = Math.max(0.08 * dxfScale, columnRadius * 0.12);
    const crossStrokeWidth = Math.max(0.05 * dxfScale, columnRadius * 0.06);
    // Keep the X fully inside the circle and slightly inset from the edge.
    const crossInsetFactor = 0.85;
    const crossHalfExtent = Math.max(0, (columnRadius - crossStrokeWidth / 2) * crossInsetFactor) / Math.sqrt(2);

    const armX = 0;
    const armY = 0;
    const baseDirY = stageYUp ? 1 : -1; // north in stage coords
    const armTipX = armX;
    const armTipY = armY + (effectiveArmLength * baseDirY);

    const rectX = armX;
    const rectY = armY + (rectArmLength * baseDirY);

    const color = props.color === "#FFA500 " ? "orange" : props.color;

    useEffect(() => {
        if (textRef.current) {
            const box = textRef.current.getClientRect();
            setTextWidth(box.width / props.stageScale);
        }
    }, [props.name, props.stageScale, hoverColumn]);

    const textStageX = scaledX - (textWidth / 2);
    const textStageY = props.textPosY;
    const textFlipProps = stageYUp
        ? { scaleY: -1, offsetY: getDynamicFontSize() }
        : {};

    return (
        <>
            <Group
                x={scaledX}
                y={scaledY}
                rotation={positionAngle}
                onMouseEnter={() => setHoverColumn(true)}
                onMouseLeave={() => setHoverColumn(false)}
            >
                {/* Arm Line */}
                <Line
                    points={[
                        armX,
                        armY,
                        armTipX,
                        armTipY,
                    ]}
                    stroke={color}
                    strokeWidth={armStrokeWidth}
                />

                {/* Lantern Box */}
                <Rect
                    x={rectX}
                    y={rectY}
                    width={luminaireMarkerSize}
                    height={luminaireMarkerSize}
                    fill={color}
                    offset={{ x: luminaireMarkerSize / 2, y: luminaireMarkerSize / 2 }}
                />

                {/* Draggable Column Base */}
                <Circle
                    x={0}
                    y={0}
                    radius={columnRadius}
                    fill={color}
                    listening={false}
                />

            </Group>

            {/* Keep center cross upright (not affected by mast/column rotation) */}
            <Line
                points={[
                    scaledX - crossHalfExtent, scaledY - crossHalfExtent,
                    scaledX + crossHalfExtent, scaledY + crossHalfExtent,
                ]}
                stroke="black"
                strokeWidth={crossStrokeWidth}
                lineCap="round"
                listening={false}
            />
            <Line
                points={[
                    scaledX - crossHalfExtent, scaledY + crossHalfExtent,
                    scaledX + crossHalfExtent, scaledY - crossHalfExtent,
                ]}
                stroke="black"
                strokeWidth={crossStrokeWidth}
                lineCap="round"
                listening={false}
            />

            {/* Drag/transform handle limited to base circle only */}
            <Rect
                ref={groupRef}
                x={scaledX}
                y={scaledY}
                width={columnRadius * 2}
                height={columnRadius * 2}
                offset={{ x: columnRadius, y: columnRadius }}
                rotation={positionAngle}
                fill="rgba(0,0,0,0.001)"
                draggable={props.selected && !isLocked}
                onDragMove={handleDragMove}
                onDragEnd={(e) => props.updateColumn(e, props.i)}
                onClick={handleSelect}
                onTap={handleSelect}
                onMouseEnter={() => setHoverColumn(true)}
                onMouseLeave={() => setHoverColumn(false)}
            />

            {/* Optional Hover Text (kept outside transformer group) */}
            {hoverColumn && (
                <Text
                    ref={textRef}
                    x={textStageX}
                    y={textStageY}
                    text={props.name}
                    fontSize={getDynamicFontSize()}
                    fill="black"
                    fontStyle="bold"
                    padding={5}
                    backgroundColor="gray"
                    opacity={0.75}
                    listening={false}
                    {...textFlipProps}
                />
            )}
        </>
    );
};

export default NormalColumn;
